import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight, ChefHat, Home as HomeIcon, Users, Star, Quote, Phone,
  MessageCircle, ChevronDown, Gift,
} from "lucide-react";
import {
  ScrollFortschritt, WortReveal, Laufband, ParallaxBild, VorhangBild, BildStory,
  Lichtzeiger, useSmoothScroll, LinieTitel, FotoLaufband,
} from "@/components/vorschau/WowBausteine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTRO_BILDER = [
  { src: "/hero-portrait-breit.jpg", pos: "50% 50%",
    handy: "/hero-portrait-handy.jpg", handyPos: "50% 12%", alt: "Lukas Stutz" },
  { src: "/anrichten-sauce.jpg", pos: "60% 50%",
    handy: "/anrichten-sauce.jpg", handyPos: "55% 50%", alt: "Sauce wird angerichtet" },
  { src: "/hero-jakobsmuschel-breit.jpg", pos: "50% 50%",
    handy: "/hero-jakobsmuschel-handy.jpg", handyPos: "50% 40%", alt: "Jakobsmuschel mit Kürbis" },
  { src: "/event-bg-teller.jpg", pos: "55% 45%",
    handy: "/event-bg-teller.jpg", handyPos: "50% 45%", alt: "Angerichtete Teller" },
];

const SZENEN = [
  {
    kicker: "Schritt 01 · Vormittags", titel: "Die Vorbereitung",
    bild: "/einkauf-markt.jpg",
    text: "Alles beginnt beim Einkauf: Gemüse, Fisch und Fleisch wähle ich selbst aus — frisch, saisonal und in der Menge, die wirklich gebraucht wird. Danach entstehen Fonds, Saucen und Patisserie von Hand.",
    punkte: ["Einkauf beim Händler und auf dem Markt", "Eigene Messer, eigene Pfannen", "Absprache mit Ihrem Team vor dem Service"],
    fussnote: "Planung und Mise en place sind im Preis enthalten.",
  },
  {
    kicker: "Schritt 02 · Am Pass", titel: "Jeder Teller von Hand",
    bild: "/event-bg-teller.jpg",
    text: "Angerichtet wird Teller für Teller, nicht am Fließband. Der dreißigste Gast bekommt denselben Teller wie der erste — gleiche Temperatur, gleiche Menge, gleiche Sorgfalt.",
    punkte: ["Gleichmäßige Portionen für jeden Gast", "Kurze Wege, ruhiger Ablauf am Pass", "Rücksicht auf Allergien und Wünsche"],
    fussnote: "Von zwei bis über hundert Gäste, je nach Küche.",
  },
  {
    kicker: "Schritt 03 · Abends", titel: "Der Gang",
    bild: "/privatkoch-thunfisch.jpg",
    text: "Jeder Gang kommt auf den Punkt. Sie sitzen bei Ihren Gästen, ich stehe am Herd und gebe das Tempo vor — abgestimmt auf Gespräche, Reden und Pausen.",
    punkte: ["Vier bis sieben Gänge im Menü", "Saisonal, regional eingekauft", "Vegetarisch und vegan auf Wunsch"],
    fussnote: "Den Menüablauf stimmen wir vorher gemeinsam ab.",
  },
  {
    kicker: "Schritt 04 · Zum Schluss", titel: "Der süße Abschluss",
    bild: "/dessert-glaeser.jpg",
    text: "Dessert aus eigener Patisserie, danach eine aufgeräumte Küche. Der letzte Eindruck ist mir genauso wichtig wie der erste — Sie bleiben am Tisch, ich räume auf.",
    punkte: ["Dessert frisch am Abend gefertigt", "Küche sauber und abgewaschen", "Reste sauber verpackt für den nächsten Tag"],
    fussnote: "Kein Aufräumen für Sie, keine Spülberge am Morgen.",
  },
];

const WEGE = [
  {
    nr: "01", icon: ChefHat, titel: "Mietkoch für Betriebe",
    text: "Ich stehe selbst bei Ihnen am Herd — à la carte, Bankett, Frühstück oder Catering. Stundenweise und kurzfristig.",
    meta: "ab 49 € netto / Std · Werktag", to: "/anfrage-betrieb", cta: "Einsatz anfragen",
    bild: "https://images.unsplash.com/photo-1488992783499-418eb1f62d08?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    testid: "wow-weg-mietkoch",
  },
  {
    nr: "02", icon: HomeIcon, titel: "Privatkoch für Zuhause",
    text: "Ihr Menü-Abend in der eigenen Küche: Einkauf, Vorbereitung, Service und eine aufgeräumte Küche zum Abschied.",
    meta: "Festpreis pro Person · ab 2 Gästen", to: "/privatkoch", cta: "Private Dining entdecken",
    bild: "/images/04.jpg", testid: "wow-weg-privatkoch",
  },
  {
    nr: "03", icon: Users, titel: "Koch-Vermittlung",
    text: "Wenn ich selbst keine Zeit habe: geprüfte selbstständige Köche aus meinem Netzwerk — für Betriebe und Privatkunden.",
    meta: "Betriebe · Privatkunden · Festanstellung", to: "/vermittlung", cta: "Vermittlung ansehen",
    bild: "https://images.unsplash.com/photo-1676471926534-d5c9771909fa?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    testid: "wow-weg-vermittlung",
  },
];

const GALERIE = [
  { src: "/anrichten-sauce.jpg", titel: "Anrichten", text: "Sauce kommt zum Schluss auf den Teller", staerke: 45 },
  { src: "/dessert-schokoladenmousse.jpg", titel: "Schokoladenmousse", text: "Mango · Himbeere · Pistazie", staerke: 70 },
  { src: "/images/04.jpg", titel: "Jakobsmuschel", text: "Kürbis · Pilze · Shiso", staerke: 55 },
];

const BAND = [
  { src: "/images/04.jpg", titel: "Jakobsmuschel" },
  { src: "/dessert-schokoladenmousse.jpg", titel: "Dessert" },
  { src: "/anrichten-sauce.jpg", titel: "Anrichten" },
  { src: "/privatkoch-thunfisch.jpg", titel: "Thunfisch" },
  { src: "/dessert-glaeser.jpg", titel: "Patisserie" },
  { src: "/images/sauberkeit.jpg", titel: "Am Pass" },
  { src: "/event-bg-teller.jpg", titel: "Vorbereitung" },
];

const hoch = (i) => (i === 1 ? "h-[52vh] sm:h-[70vh]" : "h-[42vh] sm:h-[56vh]");

export default function StartWow() {
  useSmoothScroll();
  const [bild, setBild] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [alleBewertungen, setAlleBewertungen] = useState(false);
  const [googleUrl, setGoogleUrl] = useState("");

  useEffect(() => {
    const iv = setInterval(() => setBild((b) => (b + 1) % INTRO_BILDER.length), 3200);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    axios.get(`${API}/reviews/public`).then((r) => {
      setReviews(r.data?.reviews || []);
      setGoogleUrl(r.data?.google_url || "");
    }).catch(() => {});
  }, []);

  // Beste zuerst: hoechste Sterne, danach der aussagekraeftigste Text
  const beste = [...reviews].sort(
    (a, b) => Number(b.rating || 5) - Number(a.rating || 5) || (b.text || "").length - (a.text || "").length,
  );

  const schnitt = reviews.length
    ? (reviews.reduce((a, r) => a + Number(r.rating || 5), 0) / reviews.length).toFixed(1).replace(".", ",")
    : null;

  return (
    <div data-testid="page-vorschau-start-wow"
      className="bg-[#FDF8F1] text-[#2B2320] selection:bg-[#C8743F] selection:text-white overflow-x-clip">
      <ScrollFortschritt />
      <Lichtzeiger />

      {/* 1 · Vollbild-Intro mit wechselnden Bildern */}
      <section className="relative h-[100svh] min-h-[560px] overflow-hidden flex items-end sm:items-center bg-[#15120F]"
        data-testid="wow-intro">
        {INTRO_BILDER.map((b, i) => (
          <motion.div key={b.src} className="absolute inset-x-0 top-0 h-[54%] sm:inset-0 sm:h-full"
            initial={false}
            animate={{ opacity: bild === i ? 1 : 0, scale: bild === i ? 1.05 : 1 }}
            transition={{ opacity: { duration: 0.85 }, scale: { duration: 6.5, ease: "linear" } }}>
            <img src={b.handy} alt={b.alt} style={{ objectPosition: b.handyPos }}
              className="absolute inset-0 w-full h-full object-cover sm:hidden" />
            <img src={b.src} alt={b.alt} style={{ objectPosition: b.pos }}
              className="absolute inset-0 w-full h-full object-cover hidden sm:block" />
          </motion.div>
        ))}
        <div className="absolute inset-0 bg-[#15120F]/22 sm:bg-[#15120F]/32" />
        {/* Handy: Foto oben, darunter ruhige dunkle Flaeche - die Schrift liegt nie im Gesicht */}
        <div className="sm:hidden absolute inset-x-0 top-[30%] h-[26%] bg-gradient-to-b from-transparent to-[#15120F]" />
        <div className="absolute inset-0 hidden sm:block bg-gradient-to-r sm:from-[#15120F]/95 sm:via-[#15120F]/45 sm:to-transparent" />
        <div className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse at center, rgba(21,18,15,0) 0%, rgba(21,18,15,0.45) 100%)" }} />
        <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="absolute bottom-0 left-0 right-0 h-44 hidden sm:block bg-gradient-to-b from-transparent to-[#FDF8F1]" />

        <div className="relative w-full max-w-6xl mx-auto px-6 md:px-12 pb-24 sm:pb-0 text-center sm:text-left">
          <motion.img src="/logo.png" alt="Lukas Stutz"
            initial={{ opacity: 0, scale: 0.82 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="hidden sm:block h-20 w-20 md:h-24 md:w-24 mx-auto sm:mx-0 rounded-full bg-[#FAF8F3]/90 p-2" />

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45, duration: 1 }}
            className="text-[11px] uppercase tracking-[0.34em] text-[#F6E3D4] text-klar mt-8">
            Michelstadt · Rhein-Main · deutschlandweit auf Anfrage
          </motion.p>

          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-[#FFFBF4] mt-7 leading-[1.08] max-w-[22ch] mx-auto sm:mx-0 text-klar-stark">
            <WortReveal text="Küche auf Profi-Niveau —" delay={0.55} testid="wow-headline-1" />
            <br className="hidden sm:block" />
            <span className="text-[#E8B98F]">
              <WortReveal text="im Betrieb wie zu Hause." delay={0.95} testid="wow-headline-2" />
            </span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="max-w-lg mx-auto sm:mx-0 text-base md:text-lg text-[#FFF8EF] mt-8 font-normal leading-relaxed text-klar">
            Als Mietkoch stehe ich in Ihrer Gastronomie am Herd, als Privatkoch koche ich
            Ihr Menü zu Hause. Frisch gekocht, verlässlich geplant — und ohne Stress für Sie.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.75, duration: 1 }}
            className="flex flex-col sm:flex-row items-center sm:items-start justify-center sm:justify-start gap-4 mt-10">
            <Link to="/anfrage-betrieb" data-testid="wow-intro-cta-anfrage"
              className="group relative inline-flex items-center justify-center overflow-hidden bg-[#C8743F] text-white px-9 py-4 text-[11px] uppercase tracking-[0.22em] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(200,116,63,0.85)]">
              <span className="absolute inset-0 bg-[#2B2320] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              <span className="relative inline-flex items-center gap-3">
                Einsatz anfragen
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
            <Link to="/privatkoch" data-testid="wow-intro-cta-privat"
              className="inline-flex items-center justify-center gap-2.5 border border-white/45 text-[#FFFBF4] hover:bg-white/10 hover:border-white px-9 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors duration-300">
              Privatkoch für Zuhause
            </Link>
          </motion.div>

          {schnitt && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 1 }}
              className="mt-10 sm:mt-9 flex justify-center sm:justify-start">
              <a href="#wow-bewertungen" data-testid="wow-intro-rating"
                className="inline-flex items-center gap-2.5 leading-none text-[#EADFD2] hover:text-white transition-colors">
                <span className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={13} className="fill-[#E8B98F] text-[#E8B98F]" />
                  ))}
                </span>
                <span className="text-xs tracking-wide whitespace-nowrap text-klar">
                  {schnitt} von 5 · {reviews.length} Bewertungen
                </span>
              </a>
            </motion.div>
          )}

          <motion.span animate={{ y: [0, 7, 0] }} transition={{ duration: 2.2, repeat: Infinity }}
            className="hidden sm:flex justify-center sm:justify-start mt-8">
            <ChevronDown size={18} className="text-[#C8743F]" />
          </motion.span>
        </div>
      </section>

      {/* 2 · Laufband */}
      <div className="bg-[#FDF8F1] border-b border-[#EBDCC6]">
        <Laufband testid="wow-laufband"
          worte={["Fine Dining", "Bankett", "Private Dining", "Hochzeit", "Firmenevent",
            "Menü-Abend", "Catering", "Geburtstag", "Küchenurlaub", "Personalausfall"]} />
      </div>

      {/* 3 · Zitat */}
      <section className="max-w-3xl mx-auto px-6 py-24 sm:py-32 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          data-testid="wow-zitat">
          <Quote size={24} className="text-[#C8743F] mx-auto" />
          <p className="font-serif text-2xl sm:text-3xl md:text-4xl font-light leading-[1.35] mt-7">
            „Gutes Essen braucht keine große Bühne — nur jemanden,
            der es wirklich ernst nimmt."
          </p>
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#C8743F] mt-9">Lukas Stutz</p>
        </motion.div>
      </section>

      {/* 4 · Scroll-Story */}
      <BildStory szenen={SZENEN} testid="wow-story" />

      {/* 5 · Drei Wege */}
      <section className="bg-[#FDF8F1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 sm:py-32">
          <LinieTitel kicker="Drei Wege" titel="Wie wir zusammenarbeiten."
            className="max-w-2xl" testid="wow-wege-titel" />
          <motion.p initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 max-w-2xl text-[#6B5C50] font-light leading-relaxed">
            Wählen Sie, was zu Ihnen passt — ich melde mich persönlich, meist innerhalb von 24 Stunden.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-16">
            {WEGE.map((w, i) => (
              <motion.div key={w.nr} initial={{ opacity: 0, y: 44 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.85, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}>
                <Link to={w.to} data-testid={w.testid}
                  className="group relative block h-full bg-[#FFFDF8] border border-[#E6D8C2] hover:border-[#C8743F] hover:-translate-y-1.5 hover:shadow-[0_32px_60px_-38px_rgba(120,80,48,0.65)] transition-all duration-500 overflow-hidden">
                  <div className="h-48 overflow-hidden">
                    <img src={w.bild} alt={w.titel} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.12]" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-start justify-between">
                      <w.icon size={26} strokeWidth={1.2} className="text-[#C8743F]" />
                      <span className="font-serif text-3xl font-light text-[#E0D3C0] group-hover:text-[#C8743F] transition-colors duration-300">
                        {w.nr}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl font-light mt-6 leading-snug">{w.titel}</h3>
                    <p className="mt-4 text-sm text-[#6B5C50] font-light leading-relaxed">{w.text}</p>
                    <p className="mt-6 text-[10px] uppercase tracking-[0.2em] text-[#A08C7B]">{w.meta}</p>
                    <span className="mt-5 inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.2em] text-[#C8743F]">
                      {w.cta}
                      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                    </span>
                  </div>
                  <span className="absolute left-0 bottom-0 h-px w-0 bg-[#C8743F] transition-all duration-700 group-hover:w-full" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5b · Foto-Laufband */}
      <section className="bg-[#15120F] py-14 sm:py-20" data-testid="wow-fotoband-sektion">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between gap-4">
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#E8B98F]">Aus meinen Abenden</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A7C6E]">Zum Blättern wischen</p>
          </div>
        </div>
        <div className="mt-8 -mx-0">
          <FotoLaufband testid="wow-fotoband" bilder={BAND} />
        </div>
      </section>

      {/* 6 · Der Koch — Parallax-Split */}
      <section className="bg-[#15120F] text-[#FAF3E8]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 items-stretch">
          <VorhangBild src="/hero-portrait-4x5.jpg" alt="Lukas Stutz"
            ausrichtung="object-top" hoehe="h-[58vh] md:h-auto md:min-h-[78vh]" staerke={30}
            testid="wow-portrait" />
          <div className="px-6 md:px-14 py-20 md:py-28 flex items-center">
            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
              data-testid="wow-koch-text">
              <p className="text-[10px] uppercase tracking-[0.32em] text-[#E8B98F]">Der Koch</p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light mt-5 leading-[1.1]">
                Am Herd zu Hause.<br className="hidden sm:block" /> Und da, wo es klemmt.
              </h2>
              <p className="text-sm md:text-base text-[#D9CDBF] mt-7 font-light leading-relaxed">
                Ausgebildeter Koch, seit Jahren in fremden Küchen unterwegs — Restaurant, Hotel,
                Bankett, Private Dining. Durch die vielen Stationen sehe ich schnell, wo es in
                einem Betrieb hängt: beim Personal, im Ablauf, in der Kalkulation oder einfach
                an einem Abend, an dem jemand fehlt.
              </p>
              <p className="text-sm md:text-base text-[#D9CDBF] mt-5 font-light leading-relaxed">
                Genau dort setze ich an. Ich stehe am Herd, entwickle Menüs, kalkuliere und
                gestalte Karten, bringe Abläufe in eine ruhige Ordnung und schule Ihr Team in
                Hygiene nach HACCP. Immer in Absprache, nie über den Kopf Ihres Teams hinweg —
                mit dem Ziel, dass es auch nach meinem Einsatz rundläuft.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8">
                {["Menü-Entwicklung", "Kalkulation", "Kartengestaltung", "Ablauforganisation", "Hygieneschulung"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2.5 text-[10px] uppercase tracking-[0.2em] text-[#E8B98F]">
                    <span className="w-3 h-px bg-[#C8743F]" />{t}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mt-10">
                <Link to="/ueber-mich" data-testid="wow-koch-cta"
                  className="inline-flex items-center gap-3 border border-white/40 hover:bg-white/10 hover:border-white px-8 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors">
                  Über mich <ArrowRight size={15} />
                </Link>
                <Link to="/leistungen" data-testid="wow-koch-leistungen"
                  className="inline-flex items-center gap-3 bg-[#C8743F] text-white hover:bg-[#A95A2B] px-8 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors">
                  Leistungen
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7 · Galerie mit Parallax */}
      <section className="bg-[#F7EADA] border-y border-[#EBD9C2]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 sm:py-32">
          <LinieTitel kicker="Impressionen" titel="Teller, die in Erinnerung bleiben."
            className="max-w-2xl" testid="wow-galerie-titel" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-16 items-start">
            {GALERIE.map((g, i) => (
              <motion.figure key={g.src} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className={i === 1 ? "sm:-mt-10" : ""} data-testid={`wow-galerie-${i}`}>
                <div className="bg-[#FFFDF8] border border-[#E6D8C2] p-2.5">
                  <VorhangBild src={g.src} alt={g.titel} hoehe={hoch(i)} staerke={g.staerke} />
                </div>
                <figcaption className="px-2 pt-4">
                  <p className="font-serif text-lg">{g.titel}</p>
                  <p className="text-xs text-[#8B7A68] mt-1 italic">{g.text}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* 8 · Bewertungen */}
      {reviews.length > 0 && (
        <section id="wow-bewertungen" className="bg-[#FFFDF8] border-b border-[#EFE3D2] scroll-mt-20">
          <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 sm:py-32">
            <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8 }} className="text-center">
              <span className="flex justify-center gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={16} className="fill-[#E8B98F] text-[#E8B98F]" />
                ))}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mt-6">
                {schnitt} von 5 Sternen
              </h2>
              <p className="text-sm text-[#8B7A68] mt-4 font-light">{reviews.length} Bewertungen auf Google</p>
            </motion.div>

            <div className="max-w-3xl mx-auto mt-14 space-y-5">
              {(alleBewertungen ? beste : beste.slice(0, 1)).map((r, i) => (
                <motion.div key={r.id || i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.8, delay: i * 0.08 }}
                  className="relative bg-[#FDF8F1] border border-[#E6D8C2] p-7 sm:p-9"
                  data-testid={`wow-review-${i}`}>
                  <span className="absolute top-3 left-3 w-4 h-4 border-t border-l border-[#C8743F]/50" />
                  <span className="absolute bottom-3 right-3 w-4 h-4 border-b border-r border-[#C8743F]/50" />
                  <Quote size={18} className="text-[#C8743F]" />
                  <p className="text-sm sm:text-base text-[#5A4D43] mt-5 font-light leading-relaxed italic">„{r.text}"</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#A08C7B] mt-6">{r.betrieb || r.author}</p>
                </motion.div>
              ))}

              {beste.length > 1 && (
                <button type="button" onClick={() => setAlleBewertungen((v) => !v)}
                  data-testid="wow-reviews-toggle"
                  className="mx-auto flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A08C7B] hover:text-[#C8743F] transition-colors">
                  {alleBewertungen ? "Weniger anzeigen" : `Alle ${beste.length} Bewertungen lesen`}
                  <ChevronDown size={14}
                    className={`transition-transform duration-300 ${alleBewertungen ? "rotate-180" : ""}`} />
                </button>
              )}
            </div>

            {googleUrl && (
              <div className="text-center mt-12">
                <a href={googleUrl} target="_blank" rel="noreferrer" data-testid="wow-google-link"
                  className="inline-flex items-center gap-2.5 border border-[#D8C9B6] hover:border-[#C8743F] hover:text-[#C8743F] px-8 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors">
                  Alle Bewertungen auf Google
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 9 · Gutschein */}
      <section className="bg-[#FDF8F1]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1] }}>
            <img src="/gutschein-foto.jpg" alt="Gutschein von Lukas Stutz" loading="lazy"
              data-testid="wow-gutschein-foto"
              className="w-full shadow-[0_34px_80px_-44px_rgba(120,80,48,0.7)]" />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 36 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.9, delay: 0.1 }}>
            <Gift size={22} className="text-[#C8743F]" />
            <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-5">
              Ein Abend als Geschenk.
            </h2>
            <p className="text-sm text-[#6B5C50] mt-6 font-light leading-relaxed">
              Handschriftlich ausgefüllt, mit Wachssiegel versehen und im Umschlag per Post —
              ein Gutschein für ein Menü in der eigenen Küche bleibt länger in Erinnerung als
              jedes Päckchen.
            </p>
            <Link to="/gutscheine" data-testid="wow-gutschein-cta"
              className="inline-flex items-center gap-3 bg-[#C8743F] text-white hover:bg-[#A95A2B] px-8 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors mt-9">
              Gutschein ansehen <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 10 · Abschluss */}
      <section className="relative overflow-hidden text-[#FAF3E8]">
        <div className="absolute inset-0 bg-cover bg-center animate-kenburns"
          style={{ backgroundImage: "url('/anrichten-sauce.jpg')" }} />
        <div className="absolute inset-0 bg-[#15120F]/42" />
        <div className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse 46% 48% at 50% 48%, rgba(8,6,5,0.88) 0%, rgba(8,6,5,0.62) 55%, rgba(8,6,5,0) 100%)" }} />
        <div className="relative max-w-3xl mx-auto px-6 py-28 sm:py-36 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.9 }}>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#F6D7BE] text-klar">
              Lassen Sie uns sprechen
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-6xl font-light mt-6 leading-[1.08] text-[#FFFBF4] text-klar-stark">
              Ihr Termin ist vielleicht<br className="hidden sm:block" /> noch frei.
            </h2>
            <p className="text-sm md:text-base text-[#FFF8EF] mt-7 font-normal leading-relaxed max-w-xl mx-auto text-klar">
              Schreiben Sie mir kurz Anlass, Datum und Gästezahl — ich sage ehrlich, ob es passt,
              und melde mich meist innerhalb von 24 Stunden.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-12">
              <Link to="/anfrage-betrieb" data-testid="wow-final-cta"
                className="group relative inline-flex items-center overflow-hidden bg-[#C8743F] text-white px-9 py-4 text-[11px] uppercase tracking-[0.22em]">
                <span className="absolute inset-0 bg-[#FAF3E8] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                <span className="relative inline-flex items-center gap-3 group-hover:text-[#2B2320] transition-colors duration-500">
                  Jetzt anfragen <ArrowRight size={15} />
                </span>
              </Link>
              <Link to="/kontakt" data-testid="wow-final-kontakt"
                className="inline-flex items-center gap-2.5 border border-white/40 hover:bg-white/10 px-9 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors">
                Kontakt &amp; Erreichbarkeit
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mobile Schnellzugriff */}
      <div className="md:hidden sticky bottom-0 z-50 flex border-t border-[#E0CDB4] bg-[#FDF8F1]/95 backdrop-blur"
        data-testid="wow-mobil-leiste">
        <a href="https://wa.me/4915127282560" target="_blank" rel="noreferrer"
          data-testid="wow-mobil-whatsapp"
          className="flex-1 inline-flex items-center justify-center gap-2 py-4 text-[10px] uppercase tracking-[0.2em] text-[#2B2320]">
          <MessageCircle size={14} className="text-[#C8743F]" /> WhatsApp
        </a>
        <a href="tel:+4915127282560" data-testid="wow-mobil-anruf"
          className="flex-1 inline-flex items-center justify-center gap-2 py-4 text-[10px] uppercase tracking-[0.2em] text-[#2B2320] border-x border-[#E0CDB4]">
          <Phone size={14} className="text-[#C8743F]" /> Anrufen
        </a>
        <Link to="/anfrage-betrieb" data-testid="wow-mobil-anfrage"
          className="flex-1 inline-flex items-center justify-center gap-2 py-4 text-[10px] uppercase tracking-[0.2em] bg-[#C8743F] text-white">
          Anfragen <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
